/** 대분류명이 가점 사항(가점)인지 */
export const IsBonusCategoryLabel = (label) => {
  const compact = String(label ?? '').replace(/\s+/g, '');
  return compact.includes('가점');
};

/** steps 기준 가점 대분류 val1 집합 (예: "9") */
export const GetBonusVal1Set = (steps = []) =>
  new Set(
    (steps || [])
      .map((step, idx) => ({ step, val1: String(idx + 1) }))
      .filter(({ step }) => IsBonusCategoryLabel(step?.label))
      .map(({ val1 }) => val1)
  );

export const IsBonusVal1 = (val1, steps) =>
  GetBonusVal1Set(steps).has(String(val1));

/** 착안사항에 배점(val2)이 직접 설정되어 있는지 */
export const HasCheckpointScore = (checkpoint) =>
  checkpoint?.val2 != null && checkpoint.val2 !== '';

/** total을 count개 정수로 균등 분배 (합계 = total) */
export const DistributeIntegerScore = (total, count) => {
  const safeTotal = Math.max(0, Math.round(Number(total) || 0));
  const safeCount = Math.max(1, Math.round(Number(count) || 1));
  const base = Math.floor(safeTotal / safeCount);
  const remainder = safeTotal - base * safeCount;
  return Array.from({ length: safeCount }, (_, i) => base + (i < remainder ? 1 : 0));
};

/** 배점·취득점수 정수 표기 */
export const FormatIntegerScore = (value) =>
  Math.max(0, Math.round(Number(value) || 0));

/** 착안사항 배점 — val2 직접 입력값, 없으면 지표 배점 균등분배(레거시) */
export const GetCheckpointScore = (checkpoint, indicator, checkpoints) => {
  if (HasCheckpointScore(checkpoint)) {
    return FormatIntegerScore(checkpoint.val2);
  }
  const count = checkpoints?.length || 1;
  return FormatIntegerScore(Number(indicator?.val2 || 0) / count);
};

/** 레거시 데이터: 착안사항 val2 미설정 시 지표 배점을 균등 분배해 채움 */
export const ResolveCheckpointScores = (arrayList = [], arrayList2 = []) => {
  const normalized = (arrayList2 || []).map((row) => ({ ...row }));

  (arrayList || []).forEach((indicator) => {
    const checkpoints = normalized.filter((c) => c.groupCd === indicator.code);
    if (!checkpoints.length) return;

    const hasAnyScore = checkpoints.some((cp) => HasCheckpointScore(cp));
    if (hasAnyScore) return;

    const indicatorScore = FormatIntegerScore(indicator?.val2 || 0);
    if (indicatorScore <= 0) return;

    const shares = DistributeIntegerScore(indicatorScore, checkpoints.length);
    checkpoints.forEach((cp, idx) => {
      const rowIdx = normalized.findIndex((row) => row.code === cp.code);
      if (rowIdx >= 0) {
        normalized[rowIdx] = { ...normalized[rowIdx], val2: shares[idx] ?? 0 };
      }
    });
  });

  return normalized;
};

/** 대분류 총배점 — step.maxScore 직접 설정값, 없으면 평가지표 배점 합 */
export const GetCategoryMaxScore = (categoryIndex, steps = [], arrayList = []) => {
  const step = steps[categoryIndex];
  if (step?.maxScore != null && step.maxScore !== '') {
    return Number(step.maxScore) || 0;
  }
  const catNo = String(categoryIndex + 1);
  return (arrayList || [])
    .filter((ind) => String(ind.val1) === catNo)
    .reduce((sum, ind) => sum + Number(ind.val2 || 0), 0);
};

/** 가점 제외 대분류 총배점 합 */
export const SumCategoryMaxScore = (
  steps = [],
  arrayList = [],
  { excludeBonus = true } = {}
) =>
  (steps || []).reduce((sum, step, idx) => {
    if (excludeBonus && IsBonusCategoryLabel(step?.label)) return sum;
    return sum + GetCategoryMaxScore(idx, steps, arrayList);
  }, 0);

/** 평가지표 배점(val2) 합 */
export const SumIndicatorVal2 = (
  arrayList,
  steps,
  { excludeBonus = false } = {}
) => {
  const bonusSet = excludeBonus ? GetBonusVal1Set(steps) : new Set();
  return (arrayList || [])
    .filter((row) => !bonusSet.has(String(row.val1)))
    .reduce((sum, row) => sum + Number(row.val2 || 0), 0);
};

/** 평가지표 취득(val3) 합 */
export const SumIndicatorVal3 = (
  arrayList,
  steps,
  { excludeBonus = false, onlyBonus = false } = {}
) => {
  const bonusSet = GetBonusVal1Set(steps);
  return (arrayList || [])
    .filter((row) => {
      const isBonus = bonusSet.has(String(row.val1));
      if (onlyBonus) return isBonus;
      if (excludeBonus) return !isBonus;
      return true;
    })
    .reduce((sum, row) => sum + Number(row.val3 || 0), 0);
};

/** 착안사항 평가값 → 취득점수 (Y=양호만 배점 반영) */
export const GetObtainedByCheck = (check, score) => {
  if (check === 'Y') {
    return FormatIntegerScore(score);
  }
  return 0;
};

/** 지표·착안사항 기준 취득점수·페이지별 점수 재계산 */
export const ComputeEvaluationScores = (
  arrayList = [],
  arrayList2 = [],
  stepCount = 9
) => {
  const normalizedList2 = ResolveCheckpointScores(arrayList, arrayList2);

  const updatedList = (arrayList || []).map((indicator) => {
    const checkpoints = normalizedList2.filter((c) => c.groupCd === indicator.code);
    let totalPo = 0;
    let maxPo = 0;

    checkpoints.forEach((cp) => {
      const cpScore = GetCheckpointScore(cp, indicator, checkpoints);
      maxPo += cpScore;
      if (cp.check === 'Y') totalPo += cpScore;
    });

    return {
      ...indicator,
      val2: FormatIntegerScore(maxPo),
      val3: FormatIntegerScore(totalPo),
    };
  });

  const pagePts = {};
  for (let i = 1; i <= stepCount; i += 1) {
    pagePts[`no${i}`] = 0;
  }
  let total = 0;
  updatedList.forEach((ind) => {
    const v = Number(ind.val3 || 0);
    total += v;
    const key = `no${ind.val1}`;
    if (pagePts[key] !== undefined) pagePts[key] += v;
  });

  return {
    updatedList,
    normalizedList2,
    pagePts,
    total: FormatIntegerScore(total),
  };
};

/** 양식 저장용 기준 총점 — 가점 대분류 제외, 대분류 직접 설정 총배점 합산 */
export const CalcCategoryTotalForTemplate = (steps, arrayList) =>
  SumCategoryMaxScore(steps, arrayList, { excludeBonus: true });

/** SaveSerBasicU2 API용 formData 정규화 (타입·필드 호환) */
export const BuildTemplateFormDataForSave = (
  steps = [],
  arrayList = [],
  arrayList2 = []
) => ({
  steps: (steps || []).map((step) => {
    const next = {
      id: step.id,
      label: step.label,
    };
    if (step.maxScore != null && step.maxScore !== '') {
      next.maxScore = FormatIntegerScore(step.maxScore);
    }
    return next;
  }),
  arrayList: (arrayList || []).map((row) => ({
    code: row.code,
    codeNm: row.codeNm,
    val1: row.val1 != null ? String(row.val1) : '',
    val2: FormatIntegerScore(row.val2),
    val3: FormatIntegerScore(row.val3),
  })),
  arrayList2: (arrayList2 || []).map((row) => {
    const next = {
      code: row.code,
      groupCd: row.groupCd,
      codeNm: row.codeNm,
      check: row.check || 'N',
      val1: row.val1 != null ? String(row.val1) : '',
    };
    if (row.val2 != null && row.val2 !== '') {
      next.val2 = FormatIntegerScore(row.val2);
    }
    return next;
  }),
});
